import { fileStore } from '@/features/chats/model/draft/fileStore';
import { getMediaMetadata } from '@/infrastructure/sdk';
import { AUDIO_FORMATS } from '@/shared/constants';
import { logger } from '@/shared/utils';
import { v4 as uuidv4 } from 'uuid';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Resolve msgType from a File's MIME type.
 */
const getMsgType = file => {
   if (file.type.startsWith('image/')) return 'image';
   if (file.type.startsWith('video/')) return 'video';
   if (AUDIO_FORMATS.includes(file.type)) return 'audio';
   return 'file';
};

/**
 * Extracts duration from an audio file using the browser's Audio element.
 * Bounded by a timeout — some edge-case files (e.g. a recording cut off right at a
 * max-duration limit) can leave the browser's metadata loader stuck with neither
 * `loadedmetadata` nor `error` ever firing, which would otherwise hang this forever.
 */
const AUDIO_DURATION_TIMEOUT_MS = 5000;

const getAudioDuration = file =>
   new Promise(resolve => {
      const audio = new Audio();
      const url = URL.createObjectURL(file);
      let settled = false;

      const finish = duration => {
         if (settled) return;
         settled = true;
         clearTimeout(timeoutId);
         URL.revokeObjectURL(url);
         resolve(duration);
      };

      const timeoutId = setTimeout(() => finish(0), AUDIO_DURATION_TIMEOUT_MS);

      audio.onloadedmetadata = () => finish(Math.round(audio.duration * 1000)); // ms
      audio.onerror = () => finish(0);
      audio.src = url;
   });

/**
 * Build base metadata synchronously — no SDK, no async.
 */
const buildBaseMetadata = (fileId, file, msgType, options = {}) => ({
   fileId,
   name: file.name,
   size: file.size,
   type: file.type,
   msgType,
   blobUrl: URL.createObjectURL(file),
   caption: '',
   mentionUserIds: [],
   isCameraCapture: !!options.isCameraCapture,
   isProcessing: msgType === 'image' || msgType === 'video' || msgType === 'audio', // flag: SDK enrichment pending
   ...(msgType === 'audio' && {
      duration: file.metadata?.duration ?? 0,
      audioType: file.metadata?.audioType ?? 'file',
   }),
});

// ─── Phase 1 — Instant (sync, no SDK) ────────────────────────────────────────

/**
 * Synchronously prepares files for immediate Redux dispatch.
 * Stores File objects in fileStore and returns base metadata.
 * Images/videos get `isProcessing: true` until Phase 2 enriches them.
 *
 * @param   {File[]}  filesArray
 * @param   {Object}  options
 * @returns {{ fileIds: string[], fileMetadata: Object }}
 */
export const processFilesQuick = (filesArray, options = {}) => {
   const fileIds = [];
   const fileMetadata = {};

   for (const file of filesArray) {
      const fileId = uuidv4();
      const msgType = getMsgType(file);

      fileStore.addFile(fileId, file);
      fileIds.push(fileId);
      fileMetadata[fileId] = buildBaseMetadata(fileId, file, msgType, options);
   }

   return { fileIds, fileMetadata };
};

// ─── Phase 2 — Background SDK enrichment ─────────────────────────────────────

/**
 * Fetches SDK metadata (dimensions, thumbnail, etc.) for a single image/video.
 * Returns a partial metadata patch — caller merges into Redux.
 *
 * @param   {File}   file
 * @param   {string} msgType  'image' | 'video'
 * @returns {Promise<Object>} Metadata patch (never throws — errors are swallowed)
 */
export const extractFileMetadata = async (file, msgType) => {
   try {
      if (msgType === 'audio') {
         const duration = file.metadata?.duration || (await getAudioDuration(file));
         return { duration, isProcessing: false };
      }
      const { base64, ...sdkMeta } = await getMediaMetadata({ file, mediaType: msgType, seekTime: 1 });
      return { ...sdkMeta, thumbImage: base64, isProcessing: false };
   } catch (error) {
      logger.error('SDK metadata error for file:', file.name, error);
      return { isProcessing: false }; // graceful degradation — file still usable
   }
};
